import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth/store';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Star, Camera, Utensils, Music, Coffee, BedDouble, Globe, ExternalLink, Instagram } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';

export function JoieDeVivreExperience() {
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    const location = { lat: 43.2590, lng: 3.2804 }; // Languedoc-Roussillon, France coordinates
    const map = new google.maps.Map(mapElement, {
      center: location,
      zoom: 10,
      mapTypeId: "satellite",
      disableDefaultUI: true, 
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL
      }
    });

    new google.maps.Marker({
      position: location, 
      map,
      title: "Languedoc-Roussillon, France"
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 2.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 2500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    // Open the external booking link
    window.open("https://www.loonersjoiedevivre.com", "_blank");
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen" style={{ 
      background: 'linear-gradient(to right, #dcf5ea, #f7e3dd)'
    }}>
      <MetaTags 
        title="Joie de Vivre by Looners | rendezvous"
        description="Experience extraordinary connection at this exclusive gathering of creators, artists, and adventurers. June 5-8, 2025."
        image="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/Flyer.jpg"
        url="https://letsrendezvous.co/experiences/joiedevivre"
      />

      {/* Hero Section with Video/Image */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            {/* Desktop version (horizontal crop) */}
            <img
              src="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/Flyer.jpg"
              alt="Joie de Vivre"
              className="hidden md:block h-full w-full object-contain"
            />
            {/* Mobile version (full vertical image) */}
            <img
              src="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/Flyer.jpg"
              alt="Joie de Vivre"
              className="md:hidden h-full w-full object-contain"
            />
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/final.mp4"
            className={`h-full md:h-auto md:max-h-[80vh] max-w-full object-contain transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/Flyer.jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(29,25,24,0.3)] to-[rgba(29,25,24,0.7)]" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-black/80 backdrop-blur-sm rounded-full text-white hover:bg-black/60 transition-colors"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-black/80 backdrop-blur-sm rounded-full text-white hover:bg-black/60 transition-colors"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-display mb-4 text-black"></h1>
            <p className="text-xl md:text-2xl mb-8 text-white">A celebration of connection, creativity, and joy</p>
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="bg-black text-white hover:bg-black/80 px-8 py-4 text-lg"
            >
              RESERVE YOUR SPOT
            </Button>
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-black hover:text-black/70 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Content Section with Tabs */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Introduction Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-display mb-6 text-black">June 5-8, 2025</h2>
            <div className="space-y-4 text-black">
              <p>
                A gathering in nature, featuring <em>extraordinary food</em>, <em>music</em>, <em>art</em>, and <em>connection</em>.
              </p>
              <p>
                Limited to 150 guests to maintain the quality of experiences and interactions.
              </p>
              <p>
                Join us for four days of curated experiences in a beautiful vineyard setting in the South of France.
              </p>
            </div>
          </motion.div>
          
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* About Section */}
              <div id="about-section" className="space-y-8 text-center max-w-2xl mx-auto scroll-mt-20 mb-16">
                <h2 className="text-2xl font-display text-black">The Essence of Joie de Vivre</h2>
                <div className="space-y-6 text-black">
                  <p>
                    Joie de Vivre is an extraordinary gathering created by Looners — a community of artists, creators, and adventurers seeking meaningful connection.
                  </p>
                  <p>
                    For four days, we create an environment where genuine relationships form, creativity flourishes, and moments of beauty are shared.
                  </p>
                  <p>
                    This is not a festival or a retreat, but something unique: a carefully curated container for exceptional humans to experience joy together.
                  </p>
                </div>
                
                <div className="aspect-video rounded-lg overflow-hidden mt-12">
                  <img 
                    src="https://rendezvouswebsite.b-cdn.net/Joie%20de%20Vivre/Flyer.jpg" 
                    alt="Joie de Vivre Event" 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="mt-12">
                  <Button 
                    onClick={handlePurchaseClick}
                    className="bg-black text-white hover:bg-black/80"
                  >
                    APPLY TO JOIN
                  </Button>
                </div>
              </div>
              
              {/* Experience Section */}
              <div id="experience-section" className="space-y-8 scroll-mt-20 mb-16">
                <h2 className="text-2xl font-display text-black text-center mb-8">What to Expect</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Connection</h3>
                    <p className="text-black">
                      Intimate gatherings, shared meals, and meaningful conversations with an exceptional community. We prioritize quality interactions and create spaces for authentic connection.
                    </p>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Nourishment</h3>
                    <p className="text-black">
                      Thoughtfully prepared meals using local ingredients, communal dining experiences, and culinary workshops that celebrate the joy of breaking bread together.
                    </p>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Art & Music</h3>
                    <p className="text-black">
                      Performances by talented musicians, interactive art installations, and opportunities to engage with creators. Music and art serve as the backdrop to our shared experience.
                    </p>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Nature</h3>
                    <p className="text-black">
                      Time to connect with the natural world through vineyard walks, Mediterranean views, and outdoor gatherings. The setting itself is an essential part of the experience.
                    </p>
                  </div>
                </div>
                
                <div className="bg-white/70 p-6 shadow-md mt-8">
                  <h3 className="text-xl font-display mb-4 text-black">Daily Flow</h3>
                  <p className="text-black mb-4">
                    While each day offers unique experiences, here's a general rhythm of what to expect:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-black">
                    <div>
                      <p className="font-medium text-black">Morning</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Optional movement practices</li>
                        <li>Communal breakfast</li>
                        <li>Workshops and activities</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-black">Afternoon</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Shared lunch experience</li>
                        <li>Free time for regional exploration</li>
                        <li>Creative sessions and talks</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-black">Evening</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Sunset gatherings</li>
                        <li>Dinner beneath the stars</li>
                        <li>Music and performances</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-black">Night</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Fireside conversations</li>
                        <li>Dancing under the stars</li>
                        <li>Late-night connections</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Location Section */}
              <div id="location-section" className="space-y-8 scroll-mt-20 mb-16">
                <div className="text-center max-w-2xl mx-auto mb-8">
                  <h2 className="text-2xl font-display text-black">Our Setting</h2>
                  <p className="text-black mt-4">
                    Joie de Vivre unfolds at a historic French château in the Languedoc-Roussillon region, 
                    nestled among vineyards in the beautiful South of France.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                  <div>
                    <div id="venue-map" className="w-full h-[350px] rounded-lg overflow-hidden shadow-md mb-4"></div>
                    <p className="text-black text-sm text-center">Languedoc-Roussillon, South of France</p>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-white/70 p-6 shadow-md">
                      <h3 className="text-xl font-display mb-4 text-black">The Space</h3>
                      <p className="text-black">
                        Our venue features a 17th-century château, expansive vineyard grounds,
                        and multiple gathering spaces both indoors and outdoors. The property provides the perfect 
                        balance of historic charm and luxurious amenities amidst stunning Mediterranean landscapes.
                      </p>
                    </div>
                    
                    <div className="bg-white/70 p-6 shadow-md">
                      <h3 className="text-xl font-display mb-4 text-black">Getting Here</h3>
                      <ul className="space-y-3 text-black">
                        <li className="flex items-start gap-2">
                          <span>•</span>
                          <span>Fly to Montpellier-Méditerranée Airport (1 hour drive)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span>•</span>
                          <span>Alternative: Toulouse-Blagnac Airport (2 hour drive)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span>•</span>
                          <span>Shuttle service available from major airports</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Details Section */}
              <div id="details-section" className="space-y-8 scroll-mt-20">
                <div className="text-center max-w-2xl mx-auto mb-8">
                  <h2 className="text-2xl font-display text-black">The Details</h2>
                  <p className="text-black mt-4">
                    Everything you need to know about participating in Joie de Vivre.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Your Full Experience Package Includes...</h3>
                    <ul className="space-y-6 text-black">
                      <li className="flex items-start gap-3">
                        <span className="text-[#d687b4] text-xl">🎈</span>
                        <span>Four night stay in 17th century castle or a luxury château accommodation with breakfast each day</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-[#d687b4] text-xl">🎈</span>
                        <span>Full access to all festival programming, including an array of incredible parties taking place in unique locations, beautiful moments, and novel surprises</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-[#d687b4] text-xl">🎈</span>
                        <span>Tethered hot air balloon ride with DJ playing live in the sky alongside you (weather permitting!)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-[#d687b4] text-xl">🎈</span>
                        <span>Wellness programming and unique sound experiences</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-[#d687b4] text-xl">🎈</span>
                        <span>Nightly dinner banquets, delectable daytime spreads</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">FAQ</h3>
                    <div className="space-y-4 text-black">
                      <div>
                        <p className="font-medium text-black">Is this a festival or conference?</p>
                        <p>Neither. This is a gathering created specifically for connection and shared experiences among kindred spirits.</p>
                      </div>
                      <div>
                        <p className="font-medium text-black">Can I come for just part of the event?</p>
                        <p>We encourage full participation to truly experience the journey we've created, but please contact us if you have special circumstances.</p>
                      </div>
                      <div>
                        <p className="font-medium text-black">Will there be alcohol?</p>
                        <p>Yes, there will be a curated selection of local wines and cocktails available for purchase.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">What to Bring</h3>
                    <ul className="space-y-2 text-black">
                      <li className="flex items-start gap-2">
                        <span>•</span>
                        <span>Comfortable clothing for warm Mediterranean days and cooler evenings</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span>•</span>
                        <span>Swimwear for pool and nearby beach activities</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span>•</span>
                        <span>One or two "elevated" outfits for evening gatherings</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span>•</span>
                        <span>Refillable water bottle</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span>•</span>
                        <span>Open mind and playful spirit</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/70 p-6 shadow-md">
                    <h3 className="text-xl font-display mb-4 text-black">Contact</h3>
                    <div className="space-y-4 text-black">
                      <p>
                        For any questions about Joie de Vivre, please reach out to us:
                      </p>
                      <p>
                        <a href="mailto:hello@loonersjoiedevivre.com" className="text-black hover:underline font-medium">hello@loonersjoiedevivre.com</a>
                      </p>
                      <div className="flex items-center gap-4 mt-4">
                        <a 
                          href="https://www.instagram.com/looners/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-black hover:text-black/80 transition-colors"
                        >
                          <Instagram className="h-5 w-5" />
                          <span>@looners</span>
                        </a>
                        <a 
                          href="https://www.loonersjoiedevivre.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-black hover:text-black/80 transition-colors"
                        >
                          <Globe className="h-5 w-5" />
                          <span>Website</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-white/70 p-6 shadow-md">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display text-black">Price</h3>
                    <div className="text-2xl font-display text-black">$2,200 USD</div>
                  </div>
                  <div className="space-y-4 text-black">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      <span>4 days, 3 nights</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      <span>June 5-8, 2025</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      <span>Limited to 150 participants</span>
                    </div>
                    <Button
                      className="w-full mt-4 bg-black text-white hover:bg-black/80"
                      onClick={handlePurchaseClick}
                    >
                      RESERVE YOUR SPOT
                    </Button>
                  </div>
                </div>
                
                <div className="bg-white/70 p-6 shadow-md">
                  <h3 className="text-xl font-display mb-4 text-black">Host</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/64c825ff69ccea70b79c5d2e/a68e9ee2-e9ad-4e4d-9ac8-3d8be741b19e/LOGO.png" 
                      alt="Looners"
                      className="w-16 h-16 rounded-full object-contain bg-white"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=LOONERS&backgroundColor=ffffff&textColor=000000";
                      }}
                    />
                    <div>
                      <p className="text-black font-medium">Looners</p>
                      <p className="text-black/70 text-sm">Experience Creators</p>
                    </div>
                  </div>
                  <p className="text-black text-sm">
                    Looners curates extraordinary experiences that connect like-minded individuals through art, culture, and shared moments. We believe in the power of gathering to create meaningful connections and unforgettable memories.
                  </p>
                </div>
                
                <div className="bg-white/70 p-6 shadow-md">
                  <h3 className="text-xl font-display mb-4 text-black">The Experience</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-[#d687b4] text-xl">🎈</span>
                      <span className="text-black">Four nights of immersive programming</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#d687b4] text-xl">🎈</span>
                      <span className="text-black">Gourmet French cuisine and wine tasting</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#d687b4] text-xl">🎈</span>
                      <span className="text-black">Access to vineyard and Mediterranean activities</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#d687b4] text-xl">🎈</span>
                      <span className="text-black">Carefully curated entertainment and music</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#d687b4] text-xl">🎈</span>
                      <span className="text-black">Connection with extraordinary humans</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 p-12 text-center border-2 border-black max-w-3xl mx-auto" style={{
            background: 'linear-gradient(to right, rgba(220, 245, 234, 0.7), rgba(247, 227, 221, 0.7))'
          }}>
            <h2 className="text-3xl font-display mb-6 text-black">Join Us</h2>
            <p className="text-black mb-10 max-w-lg mx-auto">
              "In the end, it's not the years in your life that count. It's the life in your years."
            </p>
            <Button 
              onClick={handlePurchaseClick}
              className="text-lg px-8 py-4 bg-black text-white hover:bg-black/80"
            >
              APPLY TO JOIN • JUNE 5-8, 2025
            </Button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}